import { useListingStepNavigation } from '@/lib/hooks/use-listing-step-navigation'
import { useState, useCallback, useMemo, useRef } from 'react'
import { View, Text } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useTranslation } from 'react-i18next'
import { notification, selection, NotificationType } from '@/lib/utils/haptics'
import Animated from 'react-native-reanimated'
import { AlertCircle, Camera, CheckCircle2, RefreshCw, ShieldCheck, X } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { PhotoLightbox, usePhotoLightbox } from '@/components/ui/PhotoLightbox'
import { Button } from '@/components/ui/Button'
import { Image as ExpoImage } from 'expo-image'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { FormActionBar } from '@/components/ui/FormActionBar'
import { ProgressBar } from '@/components/ui/ProgressBar'
import { LiveCameraModal, type LiveCameraCapture } from '@/components/camera/LiveCameraModal'
import { useListingWizardStore } from '@/lib/stores/listing-wizard.store'
import { vehiclePhotosSchema } from '@/lib/validations/vehicle.schema'
import {
  LISTING_PHOTO_REQUIREMENTS,
  MAX_PHOTOS_PER_REPEATABLE_SLOT,
  MIN_LISTING_PHOTOS,
  REPEATABLE_PHOTO_REQUIREMENT_IDS,
  type ListingPhoto,
  type ListingPhotoRequirementId,
} from '@/lib/types/listing-photo.types'
import { deleteListingPhotoFile, persistListingCapture } from '@/lib/utils/listing-photo-storage'
import { queueListingPhotoStaging } from '@/lib/utils/listing-photo-staging'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useEntrance } from '@/lib/hooks/use-entrance'

const requirementOrder = new Map(
  LISTING_PHOTO_REQUIREMENTS.map((requirement, index) => [requirement.id, index]),
)

/** Above this many missing shots we give the count instead of listing every name. */
const MAX_NAMED_MISSING_SLOTS = 4

export default function NewListingPhotosScreen() {
  const enter = useEntrance()
  const { t } = useTranslation()
  const { continueEditing, continueLabel, returnsToReview } = useListingStepNavigation('location')
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const photos = useListingWizardStore((s) => s.photos)
  const setPhotos = useListingWizardStore((s) => s.setPhotos)
  const { isDesktop, isMobile } = useBreakpoint()
  const [error, setError] = useState<string | null>(null)
  // Set once the host has actually tried to advance: only then do we paint the
  // empty slots red, so a first visit does not open on a wall of errors.
  const [showMissingSlots, setShowMissingSlots] = useState(false)
  const [activeRequirementId, setActiveRequirementId] = useState<ListingPhotoRequirementId | null>(
    null,
  )
  // True when the open camera is adding an OPTIONAL extra to a repeatable slot
  // rather than filling/retaking the slot's required photo.
  const [isAppendCapture, setIsAppendCapture] = useState(false)
  const lightbox = usePhotoLightbox()
  const scrollRef = useRef<ScrollView>(null)

  const currentPhotos = useMemo(() => photos.photos ?? [], [photos.photos])
  // Progress counts FILLED SLOTS, not photos: optional extras on repeatable slots
  // must never push the counter past 17/17 or the progress bar beyond full.
  const completedCount = useMemo(
    () => new Set(currentPhotos.map((photo) => photo.requirementId)).size,
    [currentPhotos],
  )
  const progress = completedCount / MIN_LISTING_PHOTOS
  const tileWidth = isDesktop ? 236 : isMobile ? ('100%' as const) : ('48%' as const)
  const tileAspectRatio = isMobile ? 16 / 9 : 4 / 3

  // FIRST photo per slot — the one the checklist tile shows. Repeatable slots may
  // hold more; those live in `extrasByRequirement` below.
  const photoByRequirement = useMemo(() => {
    const map = new Map<ListingPhotoRequirementId, ListingPhoto>()
    for (const photo of currentPhotos) {
      if (!map.has(photo.requirementId)) map.set(photo.requirementId, photo)
    }
    return map
  }, [currentPhotos])

  // Optional extra shots on repeatable slots (accessories): everything after the
  // first for that requirement. One photo cannot show a 360 camera AND the 4x4
  // selector, so hosts can add more without them counting as required.
  const extrasByRequirement = useMemo(() => {
    const map = new Map<ListingPhotoRequirementId, ListingPhoto[]>()
    const seen = new Set<ListingPhotoRequirementId>()
    for (const photo of currentPhotos) {
      if (!seen.has(photo.requirementId)) {
        seen.add(photo.requirementId)
        continue
      }
      map.set(photo.requirementId, [...(map.get(photo.requirementId) ?? []), photo])
    }
    return map
  }, [currentPhotos])

  const activeRequirement = useMemo(
    () =>
      activeRequirementId
        ? LISTING_PHOTO_REQUIREMENTS.find((requirement) => requirement.id === activeRequirementId)
        : undefined,
    [activeRequirementId],
  )

  const updateRequirementPhoto = useCallback(
    (requirementId: ListingPhotoRequirementId, capture: LiveCameraCapture) => {
      // Move the live capture out of the evictable cache the moment it is taken.
      // Provenance is untouched (same bytes, same source/capturedAt) — this only
      // makes the file survive an app restart or an Android cache purge, so a
      // saved draft actually still has its photos.
      const nextPhoto: ListingPhoto = {
        uri: persistListingCapture(capture.uri),
        width: capture.width,
        height: capture.height,
        capturedAt: capture.capturedAt,
        source: capture.source,
        requirementId,
      }

      // Start moving the bytes NOW, while the host walks to the next shot.
      //
      // Publishing used to upload all seventeen photos in one minute over a phone connection —
      // the minute that produced every visible "no se pudo terminar de subir las fotos" of the
      // last days. The ten-plus minutes of the walk-around are when the network is idle; a
      // photo staged here becomes a server-side COPY at publish, so the final tap moves no
      // bytes at all.
      //
      // Invisible on purpose. No spinner, no toast, no state the host can see: staging that
      // fails simply leaves this photo to the direct upload at publish, which is exactly what
      // happened before staging existed. The camera stays the priority — the queue waits for
      // interactions to settle and runs strictly one at a time, so seventeen captures never
      // become seventeen concurrent uploads fighting the viewfinder.
      queueListingPhotoStaging(nextPhoto.uri)
      // Appending an OPTIONAL extra on a repeatable slot keeps what is already
      // there; a normal capture/retake replaces only the FIRST photo of that slot
      // so a retake never silently deletes the host's extra accessory shots.
      let next: ListingPhoto[]
      if (isAppendCapture) {
        next = [...currentPhotos, nextPhoto]
      } else {
        const firstIndex = currentPhotos.findIndex((photo) => photo.requirementId === requirementId)
        if (firstIndex === -1) {
          next = [...currentPhotos, nextPhoto]
        } else {
          next = [...currentPhotos]
          // The retaken shot is orphaned the moment it leaves the draft. Delete
          // that ONE file (never a sweep) so a host who retakes several photos
          // does not accumulate full-resolution garbage on their device.
          const replaced = currentPhotos[firstIndex]
          if (replaced && replaced.uri !== nextPhoto.uri) deleteListingPhotoFile(replaced.uri)
          next[firstIndex] = nextPhoto
        }
      }
      // Stable sort by checklist order; photos sharing a slot keep their order.
      const updated = next
        .map((photo, index) => ({ photo, index }))
        .sort(
          (a, b) =>
            (requirementOrder.get(a.photo.requirementId) ?? 0) -
              (requirementOrder.get(b.photo.requirementId) ?? 0) || a.index - b.index,
        )
        .map((entry) => entry.photo)

      setPhotos({ photos: updated })
      setError(null)
      const filled = new Set(updated.map((photo) => photo.requirementId))
      // Filled SLOTS, not photo count — extras must not mark the checklist done.
      if (filled.size >= MIN_LISTING_PHOTOS) {
        setShowMissingSlots(false)
      }

      // ONE camera session for the whole checklist. Closing after every shot
      // tore down and cold-started the native camera 17 times and dumped the host
      // back to the grid to hunt the next tile — on the step that drives the most
      // abandonment. Auto-advance to the next unfilled slot and keep the camera
      // open; it closes when the checklist is complete or the host taps out.
      //
      // Adding an OPTIONAL extra is a deliberate one-off, so that path still
      // closes rather than marching the host through the required list again.
      const nextUnfilled = isAppendCapture
        ? undefined
        : LISTING_PHOTO_REQUIREMENTS.find((requirement) => !filled.has(requirement.id))
      setActiveRequirementId(nextUnfilled ? nextUnfilled.id : null)
      setIsAppendCapture(false)
      void selection()
    },
    [currentPhotos, setPhotos, isAppendCapture],
  )

  const handleSlotPress = useCallback((requirementId: ListingPhotoRequirementId) => {
    setIsAppendCapture(false)
    setActiveRequirementId(requirementId)
    void selection()
  }, [])

  /** Optional extra shot on a repeatable slot (accessories). */
  const handleAddExtraPress = useCallback((requirementId: ListingPhotoRequirementId) => {
    setIsAppendCapture(true)
    setActiveRequirementId(requirementId)
    void selection()
  }, [])

  const openListingLightbox = useCallback(
    (uri: string) => {
      const images = currentPhotos.map((photo) => ({
        id: photo.uri,
        uri: photo.uri,
      }))
      const index = images.findIndex((image) => image.id === uri)
      lightbox.open(images, index < 0 ? 0 : index)
    },
    [currentPhotos, lightbox],
  )

  const handleRemoveExtra = useCallback(
    (uri: string) => {
      // The file is orphaned the moment the draft stops referencing it.
      deleteListingPhotoFile(uri)
      setPhotos({ photos: currentPhotos.filter((photo) => photo.uri !== uri) })
      void selection()
    },
    [currentPhotos, setPhotos],
  )

  const handleNext = useCallback(() => {
    const result = vehiclePhotosSchema.safeParse({ photos: currentPhotos })
    if (!result.success) {
      // The schema only ever says "complete the 17 shots" — and it said it at
      // the very bottom of a 17-tile scroll, under a fixed action bar, so
      // tapping Siguiente looked like it did nothing at all. Name the shots
      // that are actually missing and put the message where the thumb is.
      const missing = LISTING_PHOTO_REQUIREMENTS.filter(
        (requirement) => !photoByRequirement.has(requirement.id),
      )
      const message =
        missing.length === 0
          ? // Everything is captured but the schema still rejected it (gallery
            // photo, duplicate slot) — show what it actually complained about.
            Array.from(new Set(result.error.issues.map((i) => t(i.message)))).join(' ')
          : missing.length <= MAX_NAMED_MISSING_SLOTS
            ? t('listing.photos_missing_summary', {
                missing: missing.length,
                list: missing.map((requirement) => t(requirement.labelKey)).join(', '),
              })
            : // Naming all 17 is a wall of text; the red tiles carry the detail.
              t('listing.photos_missing_count', { missing: missing.length })

      setError(message)
      setShowMissingSlots(true)
      void notification(NotificationType.Error)
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({ y: 0, animated: true })
      })
      return
    }

    setError(null)
    setShowMissingSlots(false)
    setPhotos(result.data)
    continueEditing()
  }, [currentPhotos, photoByRequirement, setPhotos, continueEditing, t])

  return (
    <ScreenContainer variant="content" scroll={false}>
      <ScrollView
        ref={scrollRef}
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        contentInsetAdjustmentBehavior="never"
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{
          padding: SPACING.lg,
          // The action bar is outside the scroller. Leave its full visual
          // footprint plus safe area so the final checklist tile can scroll
          // completely above the sticky CTA instead of being clipped.
          paddingBottom: SPACING['3xl'] + SPACING['2xl'] + SPACING.sm + 112,
        }}
      >
        <Animated.View
          collapsable={false}
          entering={enter(1)}
          style={{
            padding: SPACING.md,
            borderRadius: RADIUS.lg,
            backgroundColor: colors.surface,
            borderWidth: 1,
            borderColor: colors.border,
            gap: SPACING.md,
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.sm }}>
            <View
              style={{
                width: 38,
                height: 38,
                borderRadius: RADIUS.full,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: `${COLORS.coral.DEFAULT}14`,
              }}
            >
              <ShieldCheck size={19} color={COLORS.coral.DEFAULT} strokeWidth={2} />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text
                style={{
                  fontSize: 17,
                  lineHeight: 23,
                  fontFamily: FONTS.bodyBold,
                  color: colors.textPrimary,
                }}
              >
                {t('listing.photos_live_title')}
              </Text>
              <Text
                style={{
                  marginTop: 2,
                  fontSize: 13,
                  lineHeight: 19,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                }}
              >
                {t('listing.photos_live_subtitle', { count: MIN_LISTING_PHOTOS })}
              </Text>
            </View>
          </View>

          {/* The counter belongs to the progress track, not to the title. Sat
              beside a 17pt bold headline that already ran to the card padding,
              it left ~14pt of clearance — and "10/17" would have collided with
              it or forced the title to wrap. On the track it also gives the
              bar the label it never had. */}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <ProgressBar
                value={progress}
                height={6}
                fillColor={
                  completedCount === MIN_LISTING_PHOTOS ? colors.success : COLORS.coral.DEFAULT
                }
                trackColor={colors.surfaceSubtle}
                borderRadius={RADIUS.full}
              />
            </View>
            <Text
              style={{
                fontSize: 13,
                fontFamily: FONTS.body,
                color: completedCount === MIN_LISTING_PHOTOS ? colors.success : colors.textAccent,
              }}
            >
              {completedCount}/{MIN_LISTING_PHOTOS}
            </Text>
          </View>
        </Animated.View>

        {error ? (
          <View
            accessibilityRole="alert"
            accessibilityLiveRegion="assertive"
            testID="photos.error"
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: SPACING.sm,
              marginTop: SPACING.md,
              padding: SPACING.md,
              borderRadius: RADIUS.md,
              backgroundColor: `${COLORS.coral.DEFAULT}15`,
              borderWidth: 1,
              borderColor: `${COLORS.coral.DEFAULT}55`,
            }}
          >
            <AlertCircle size={18} color={COLORS.coral.DEFAULT} strokeWidth={2} />
            <Text
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                lineHeight: 19,
                fontFamily: FONTS.body,
                color: colors.textPrimary,
              }}
            >
              {error}
            </Text>
          </View>
        ) : null}

        <Animated.View collapsable={false} entering={enter(2)} style={{ marginTop: SPACING.lg }}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
            {LISTING_PHOTO_REQUIREMENTS.map((requirement, index) => {
              const photo = photoByRequirement.get(requirement.id)
              const isMissing = showMissingSlots && !photo
              const label = t(requirement.labelKey)
              const helper = t(requirement.helperKey)

              return (
                <Pressable
                  key={requirement.id}
                  onPress={() => handleSlotPress(requirement.id)}
                  accessibilityRole="button"
                  accessibilityLabel={
                    photo
                      ? `${label} - ${t('listing.retake_live_photo')}`
                      : `${t('listing.capture_live_photo')} - ${label}`
                  }
                  testID={`photos.slot-${requirement.id}`}
                  style={({ pressed }) => ({
                    width: tileWidth,
                    aspectRatio: tileAspectRatio,
                    borderRadius: RADIUS.lg,
                    overflow: 'hidden',
                    borderWidth: 1,
                    borderColor: photo
                      ? `${colors.success}88`
                      : isMissing
                        ? colors.error
                        : colors.border,
                    borderStyle: photo ? 'solid' : 'dashed',
                    backgroundColor: isDark ? colors.surface : colors.surfaceSubtle,
                    opacity: pressed ? 0.78 : 1,
                  })}
                >
                  {photo ? (
                    // Inner clip wrapper WITHOUT a border: RN Android does not
                    // reliably clip a child image to a rounded parent when that
                    // parent also has a borderWidth (the teal outline above), so
                    // the photo's square corners bled past the radius. A
                    // borderless overflow:hidden wrapper clips reliably, and the
                    // image carries its own borderRadius as a New-Arch backstop.
                    <View style={{ flex: 1, borderRadius: RADIUS.lg, overflow: 'hidden' }}>
                      <ExpoImage
                        source={photo.uri}
                        style={{ width: '100%', height: '100%', borderRadius: RADIUS.lg }}
                        contentFit="cover"
                      />
                      <View
                        style={{
                          position: 'absolute',
                          top: SPACING.xs,
                          left: SPACING.xs,
                          width: 28,
                          height: 28,
                          borderRadius: RADIUS.full,
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: colors.success,
                        }}
                      >
                        <CheckCircle2 size={16} color="#FFFFFF" strokeWidth={2.4} />
                      </View>
                      <View
                        style={{
                          position: 'absolute',
                          left: 0,
                          right: 0,
                          bottom: 0,
                          padding: SPACING.sm,
                          backgroundColor: 'rgba(0,0,0,0.58)',
                          gap: 2,
                        }}
                      >
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: SPACING.xs,
                          }}
                        >
                          <RefreshCw size={13} color="#FFFFFF" strokeWidth={2} />
                          <Text
                            style={{
                              flex: 1,
                              minWidth: 0,
                              fontSize: 12,
                              lineHeight: 16,
                              fontFamily: FONTS.body,
                              color: '#FFFFFF',
                            }}
                            numberOfLines={2}
                          >
                            {label}
                          </Text>
                        </View>
                      </View>
                    </View>
                  ) : (
                    // Same shape as the documents dropzone two steps later:
                    // the camera and the prompt sit together in the middle of
                    // the well, so the whole box reads as one tap target. It
                    // used to park a 18pt glyph in the top-right corner, the
                    // number in the top-left and the label on the floor, with
                    // the entire middle of every one of the 17 slots empty.
                    <View
                      style={{
                        flex: 1,
                        padding: SPACING.sm,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: SPACING.xs,
                      }}
                    >
                      <Text
                        style={{
                          position: 'absolute',
                          top: SPACING.sm,
                          left: SPACING.sm,
                          fontSize: 12,
                          fontFamily: FONTS.body,
                          color: colors.textMuted,
                        }}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </Text>
                      <Camera size={26} color={COLORS.coral.DEFAULT} strokeWidth={1.8} />
                      <Text
                        style={{
                          fontSize: 13,
                          lineHeight: 17,
                          textAlign: 'center',
                          fontFamily: FONTS.body,
                          color: colors.textPrimary,
                        }}
                        numberOfLines={isMobile ? 3 : 2}
                      >
                        {label}
                      </Text>
                      <Text
                        style={{
                          fontSize: 12,
                          lineHeight: 16,
                          textAlign: 'center',
                          fontFamily: FONTS.body,
                          color: colors.textSecondary,
                        }}
                        numberOfLines={2}
                      >
                        {helper}
                      </Text>
                    </View>
                  )}
                </Pressable>
              )
            })}
          </View>

          {/* OPTIONAL extra accessory shots. A single photo cannot show a 360
              camera, the 4x4 selector and a subwoofer at once — a real host asked
              for this. These never count toward the required checklist. */}
          <View style={{ marginTop: SPACING.lg, gap: SPACING.sm }}>
            <Text
              style={{
                fontSize: 15,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
            >
              {t('listing.extra_photos_title')}
            </Text>
            <Text
              style={{
                fontSize: 13,
                lineHeight: 18,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
              }}
            >
              {t('listing.extra_photos_help')}
            </Text>

            {REPEATABLE_PHOTO_REQUIREMENT_IDS.map((requirementId) => {
              const requirement = LISTING_PHOTO_REQUIREMENTS.find((r) => r.id === requirementId)
              if (!requirement) return null
              const extras = extrasByRequirement.get(requirementId) ?? []
              const hasBase = photoByRequirement.has(requirementId)
              const atCap = extras.length + (hasBase ? 1 : 0) >= MAX_PHOTOS_PER_REPEATABLE_SLOT
              return (
                <View key={`extra-${requirementId}`} style={{ gap: SPACING.xs }}>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: SPACING.sm,
                    }}
                  >
                    <Text
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: 13,
                        fontFamily: FONTS.body,
                        color: colors.textPrimary,
                      }}
                      numberOfLines={2}
                    >
                      {t(requirement.labelKey)}
                    </Text>
                    <Pressable
                      onPress={() => handleAddExtraPress(requirementId)}
                      disabled={!hasBase || atCap}
                      accessibilityRole="button"
                      accessibilityLabel={`${t('listing.extra_photos_add')} — ${t(requirement.labelKey)}`}
                      testID={`photos.add-extra-${requirementId}`}
                      style={({ pressed }) => ({
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: SPACING.xs,
                        minHeight: 44,
                        paddingHorizontal: SPACING.md,
                        borderRadius: RADIUS.full,
                        borderWidth: 1,
                        borderColor: !hasBase || atCap ? colors.border : colors.textAccent,
                        opacity: pressed ? 0.75 : !hasBase || atCap ? 0.5 : 1,
                      })}
                    >
                      <Camera
                        size={16}
                        color={!hasBase || atCap ? colors.textMuted : colors.textAccent}
                        strokeWidth={2}
                      />
                      <Text
                        style={{
                          fontSize: 13,
                          fontFamily: FONTS.body,
                          color: !hasBase || atCap ? colors.textMuted : colors.textAccent,
                        }}
                        numberOfLines={1}
                      >
                        {t('listing.extra_photos_add')}
                      </Text>
                    </Pressable>
                  </View>

                  {extras.length > 0 ? (
                    <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.sm }}>
                      {extras.map((extra, index) => (
                        <View
                          key={extra.uri}
                          style={{
                            width: 92,
                            height: 92,
                            borderRadius: RADIUS.lg,
                            overflow: 'hidden',
                            borderWidth: 1,
                            borderColor: `${colors.success}88`,
                          }}
                        >
                          <Pressable
                            onPress={() => openListingLightbox(extra.uri)}
                            accessibilityRole="button"
                            accessibilityLabel={t('vehicle_detail.view_image')}
                            testID={`photos.view-extra-${requirementId}-${index}`}
                            style={{ width: '100%', height: '100%' }}
                          >
                            <ExpoImage
                              source={{ uri: extra.uri }}
                              style={{ width: '100%', height: '100%', borderRadius: RADIUS.lg }}
                              contentFit="cover"
                            />
                          </Pressable>
                          {/* 44pt TAP AREA, not 28. hitSlop is a no-op on web and
                              is clipped by the thumbnail's overflow:hidden on
                              Android, so the real target was 28pt. The Pressable
                              is 44x44 (fits inside the 92pt tile) with the visible
                              28pt circle in its corner. */}
                          <Pressable
                            onPress={() => handleRemoveExtra(extra.uri)}
                            accessibilityRole="button"
                            accessibilityLabel={`${t('common.delete')} ${index + 1}`}
                            testID={`photos.remove-extra-${requirementId}-${index}`}
                            style={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              width: 44,
                              height: 44,
                              alignItems: 'flex-end',
                              justifyContent: 'flex-start',
                              padding: 2,
                            }}
                          >
                            <View
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: RADIUS.full,
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0,0,0,0.62)',
                              }}
                            >
                              <X size={15} color="#FFFFFF" strokeWidth={2.4} />
                            </View>
                          </Pressable>
                        </View>
                      ))}
                    </View>
                  ) : null}
                </View>
              )
            })}
          </View>
        </Animated.View>
      </ScrollView>

      <FormActionBar style={{ backgroundColor: colors.background }}>
        <Button
          onPress={handleNext}
          variant="primary"
          size="lg"
          title={
            returnsToReview
              ? continueLabel
              : t('listing.next_with_count', {
                  current: completedCount,
                  total: MIN_LISTING_PHOTOS,
                })
          }
          testID="photos.next-button"
        />
      </FormActionBar>

      <LiveCameraModal
        visible={Boolean(activeRequirement)}
        title={activeRequirement ? t(activeRequirement.labelKey) : ''}
        helper={activeRequirement ? t(activeRequirement.helperKey) : undefined}
        requiresDaylight={Boolean(
          activeRequirement &&
          [
            'front_right',
            'front_left',
            'rear_right',
            'rear_left',
            'hood_open',
            'engine_closeup',
            'rim_complete',
            'rim_serial',
            'trunk_panorama',
            'exterior_accessories',
          ].includes(activeRequirement.id),
        )}
        // The host stays inside the camera for the whole checklist now, so the
        // camera itself has to answer "how much is left?" — otherwise the only
        // progress indicator lives on the screen they no longer return to.
        progressLabel={
          activeRequirement && !isAppendCapture
            ? t('listing.photo_progress', {
                current: Math.min(completedCount + 1, MIN_LISTING_PHOTOS),
                total: MIN_LISTING_PHOTOS,
              })
            : undefined
        }
        onCancel={() => {
          setActiveRequirementId(null)
          setIsAppendCapture(false)
        }}
        onCapture={(capture) => {
          if (!activeRequirement) return
          updateRequirementPhoto(activeRequirement.id, capture)
        }}
      />
      <PhotoLightbox
        visible={lightbox.visible}
        images={lightbox.images}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
        onClose={lightbox.close}
      />
    </ScreenContainer>
  )
}
